<!-- 
ログイン画面のログインフォーム
-->

<template>
  <div>
    <v-row>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <dialog-base title="ログイン">
          <v-card-text>
            <alert :errors="errors"></alert>
            <v-row>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-subheader v-if="mailAddressLabel.length == 0"
                  >アカウント（メールアドレス）</v-subheader
                >
              </v-col>
              <v-col cols="12" sm="12" md="7" lg="7" xl="7">
                <v-text-field
                  ref="field_mail_address"
                  outlined
                  hide-details="auto"
                  dense
                  :label="mailAddressLabel"
                  v-model.trim="$v.formData.mailAddress.$model"
                  :error-messages="mailAddressErrorMessage"
                  :error-count="3"
                  @keydown.enter.exact="login"
                ></v-text-field
              ></v-col>
            </v-row>
            <v-row>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-subheader v-if="passwordLabel.length == 0"
                  >パスワード</v-subheader
                >
              </v-col>
              <v-col cols="12" sm="12" md="7" lg="7" xl="7">
                <v-text-field
                  ref="field_password"
                  outlined
                  hide-details="auto"
                  dense
                  type="password"
                  :label="passwordLabel"
                  v-model="$v.formData.password.$model"
                  :error-messages="passwordErrorMessage"
                  :error-count="3"
                  @keydown.enter.exact="login"
                ></v-text-field
              ></v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-spacer></v-spacer>
              <v-col cols="auto" sm="auto" md="7" lg="7" xl="7">
                <v-btn color="primary" @click.stop="login"> ログイン </v-btn>
                <v-btn color="primary" text @click.stop="confirmResetPassword">
                  パスワード再設定
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </dialog-base>
      </v-col>
      <v-spacer></v-spacer>
    </v-row>
    <confirm-dialog
      ref="confirm_password_reset"
      :autoClose="false"
      @on-click-ok="resetPassword"
    >
      メールアドレスのユーザーに、パスワード再発行メールを送信します。よろしいですか？
      <v-container class="mt-4">
        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-subheader v-if="mailAddressLabel.length == 0"
              >アカウント（メールアドレス）</v-subheader
            >
          </v-col>
          <v-col cols="12" sm="12" md="7" lg="7" xl="7">
            <v-text-field
              outlined
              hide-details="auto"
              dense
              :label="mailAddressLabel"
              v-model.trim="$v.resetForm.mailAddress.$model"
              :error-messages="resetFormMailAddressErrorMessage"
              :error-count="3"
              @keydown.enter.exact="resetPassword"
            ></v-text-field
          ></v-col>
        </v-row>
      </v-container>
    </confirm-dialog>
  </div>
</template>

<script>
import ValueMapper from "../../logic/common/value-mapper";
import DialogBase from "../frame/dialog-base.vue";
import Alert from "../common/alert.vue";
import ConfirmDialog from "../dialog/confirm.vue";
import Validate from "@/logic/common/validate.js";
import { required, maxLength } from "vuelidate/lib/validators";

export default {
  components: {
    alert: Alert,
    "dialog-base": DialogBase,
    "confirm-dialog": ConfirmDialog,
  },
  /**
   * 入力チェック
   */
  validations: {
    formData: {
      // メールアドレス
      mailAddress: {
        required,
        maxLength: maxLength(128),
        halfCharacter: Validate.rule.halfCharacter,
        email: Validate.rule.email,
      },
      // パスワード
      password: {
        required,
        maxLength: maxLength(20),
        halfCharacter: Validate.rule.halfCharacter,
      },
    },
    resetForm: {
      // メールアドレス
      mailAddress: {
        required,
        maxLength: maxLength(128),
        halfCharacter: Validate.rule.halfCharacter,
        email: Validate.rule.email,
      },
    },
  },
  /**
   * 画面が利用するデータ
   */
  data() {
    return {
      // ログインフォーム
      formData: {
        // メールアドレス
        mailAddress: "",
        // パスワード
        password: "",
        // 遅延チェックフラグ
        delayCheck: false,
      },
      // パスワード再設定フォーム
      resetForm: {
        // メールアドレス
        mailAddress: "",
        // 遅延チェックフラグ
        delayCheck: false,
      },
      errors: [],
    };
  },
  computed: {
    /**
     * メールアドレスの検証エラーを参照する
     * メール形式かどうかは遅延チェックで判定する
     */
    mailAddressErrorMessage() {
      return Validate.buildLocalizedErrorMessage(
        this.$v.formData.mailAddress,
        "メールアドレス",
        [
          "required",
          "maxLength",
          "halfCharacter",
          { value: "email", condition: this.formData.delayCheck },
        ],
        !this.formData.delayCheck
      );
    },
    /**
     * パスワードの検証エラーを参照する
     */
    passwordErrorMessage() {
      return Validate.buildLocalizedErrorMessage(
        this.$v.formData.password,
        "パスワード",
        ["required", "maxLength", "halfCharacter"],
        !this.formData.delayCheck
      );
    },
    /**
     * パスワード再設定フォームのメールアドレスの検証エラーを参照する
     * メール形式かどうかは遅延チェックで判定する
     */
    resetFormMailAddressErrorMessage() {
      return Validate.buildLocalizedErrorMessage(
        this.$v.resetForm.mailAddress,
        "メールアドレス",
        [
          "required",
          "maxLength",
          "halfCharacter",
          { value: "email", condition: this.resetForm.delayCheck },
        ],
        !this.resetForm.delayCheck
      );
    },
    /**
     * 画面幅に応じて入力欄左のタイトルを不可視にする
     */
    mailAddressLabel() {
      const title = "アカウント（メールアドレス）";
      return ValueMapper.map({
        xs: title,
        sm: title,
      }).getOrElse(this.$vuetify.breakpoint.name, "");
    },
    /**
     * 画面幅に応じて入力欄左のタイトルを不可視にする
     */
    passwordLabel() {
      const title = "パスワード";
      return ValueMapper.map({
        xs: title,
        sm: title,
      }).getOrElse(this.$vuetify.breakpoint.name, "");
    },
    /**
     * 背景のカードの幅を画面幅に応じて設定する
     */
    cardMinWidth() {
      const mobileMinWidth = 360;
      const minWidth = 700;
      return ValueMapper.map({
        xs: mobileMinWidth,
        sm: mobileMinWidth,
      }).getOrElse(this.$vuetify.breakpoint.name, minWidth);
    },
  },
  methods: {
    /**
     * ログイン処理を実行する
     */
    login() {
      // エラーをクリアする
      this.errors = [];
      this.formData.delayCheck = true;

      if (
        Validate.hasError([
          this.$v.formData.mailAddress,
          this.$v.formData.password,
        ])
      ) {
        return;
      }

      // ログイン処理を要求する
      this.$emit("on-request-login", {
        email: this.formData.mailAddress,
        password: this.formData.password,
        message: (message) => {
          this.errors.push(message);
        },
      });
    },
    /**
     * パスワード再設定ボタンを押下した
     */
    confirmResetPassword() {
      this.resetForm.delayCheck = false;
      this.resetForm.mailAddress = "";
      this.$v.resetForm.mailAddress.$reset();
      this.$refs.confirm_password_reset.showDialog();
    },
    /**
     * パスワード再設定処理を実施する
     */
    resetPassword() {
      // エラーをクリアする
      this.errors = [];
      this.resetForm.delayCheck = true;

      if (Validate.hasError([this.$v.resetForm.mailAddress])) {
        return;
      }

      // パスワード変更処理を要求する
      this.$emit("on-reset-password", {
        email: this.resetForm.mailAddress,
        message: (message) => {
          this.errors.push(message);
        },
      });

      //ダイアログを閉じる
      this.$refs.confirm_password_reset.hideDialog();
    },
  },
};
</script>

<style scope></style>
